import { useMemo, useState } from "react"
import { Box, HStack, Input, Spinner, Stack, Table } from "@chakra-ui/react"
import { FaSortDown, FaSortUp, FaSort } from "react-icons/fa"
import { Text } from "@/components"
import { CommissionRow } from "@/types"
import { useCommissionRows } from "@/hooks/commissions"

const fmt$ = (v: number | null) => {
  if (v == null) return "—"
  if (v < 0) return `($${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const fmtPct = (v: number | null) => {
  if (v == null) return "—"
  return `${(v * 100).toFixed(1)}%`
}

const fmtDate = (d: string | null) => {
  if (!d) return "—"
  return new Date(d).toLocaleDateString()
}

type SortKey = keyof CommissionRow
type SortDir = "asc" | "desc"

const COLS: { key: SortKey; label: string; align: "left" | "right" }[] = [
  { key: "companyName", label: "Company", align: "left" },
  { key: "clientName", label: "Client", align: "left" },
  { key: "fundName", label: "Fund", align: "left" },
  { key: "exitDate", label: "Exited", align: "left" },
  { key: "totalInvestment", label: "Investment", align: "right" },
  { key: "profit", label: "Profit", align: "right" },
  { key: "irr", label: "IRR", align: "right" },
  { key: "moic", label: "MOIC", align: "right" },
  { key: "calculatedCommission", label: "Calculated", align: "right" },
]

interface Props {
  personId: number
  dmName: string
}

export const CommissionsDealsTable = ({ personId, dmName }: Props) => {
  const { data: rows = [], isLoading } = useCommissionRows(personId)
  const [search, setSearch] = useState("")
  const [missingOnly, setMissingOnly] = useState(false)
  const [sortKey, setSortKey] = useState<SortKey>("calculatedCommission")
  const [sortDir, setSortDir] = useState<SortDir>("desc")

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    else { setSortKey(key); setSortDir("desc") }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return rows
      .filter((r) => !q || r.companyName.toLowerCase().includes(q) || r.clientName.toLowerCase().includes(q))
      .filter((r) => !missingOnly || r.calculatedCommission !== 0)
      .sort((a, b) => {
        const av = a[sortKey]
        const bv = b[sortKey]
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        const cmp = typeof av === "string" ? av.localeCompare(bv as string) : (av as number) - (bv as number)
        return sortDir === "asc" ? cmp : -cmp
      })
  }, [rows, search, missingOnly, sortKey, sortDir])


  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <FaSort style={{ opacity: 0.3 }} />
    return sortDir === "asc" ? <FaSortUp /> : <FaSortDown />
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" py={8}>
        <Spinner size="md" />
        <Text color="text.muted">Loading {dmName}'s deals...</Text>
      </Stack>
    )
  }

  return (
    <Stack gap={3}>
      <HStack flexWrap="wrap" gap={2}>
        <Input
          placeholder="Search company or client..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          maxW="280px"
          size="sm"
        />
        <Box
          as="button"
          px={3}
          py={1}
          borderRadius="md"
          fontSize="sm"
          bg={missingOnly ? "orange.100" : "gray.100"}
          color={missingOnly ? "orange.700" : "gray.700"}
          borderWidth="1px"
          borderColor={missingOnly ? "orange.300" : "gray.300"}
          cursor="pointer"
          onClick={() => setMissingOnly((v) => !v)}
        >
          Missing only {missingOnly ? "✓" : ""}
        </Box>
        <Box ml="auto">
          <Text fontSize="sm" color="text.muted">
            {filtered.length} deals
          </Text>
        </Box>
      </HStack>

      <Box overflowX="auto" borderWidth="1px" borderColor="border.default" borderRadius="md">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row bg="table.header">
              {COLS.map((col) => (
                <Table.ColumnHeader
                  key={col.key}
                  textAlign={col.align}
                  cursor="pointer"
                  userSelect="none"
                  onClick={() => handleSort(col.key)}
                  whiteSpace="nowrap"
                  px={3}
                  py={2}
                >
                  <HStack gap={1} justifyContent={col.align === "right" ? "flex-end" : "flex-start"}>
                    <Text fontSize="xs" fontWeight="semibold">{col.label}</Text>
                    <Box fontSize="xs"><SortIcon k={col.key} /></Box>
                  </HStack>
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={COLS.length} textAlign="center" py={6}>
                  <Text color="text.muted">No deals found</Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              filtered.map((r) => (
                <Table.Row
                  key={r.irrDealId}
                  _hover={{ bg: "hover.bg" }}
                  bg={r.isBackupDeal ? "purple.50" : undefined}
                >
                  <Table.Cell px={3} py={2}>
                    <Text fontSize="sm">{r.companyName}</Text>
                    {r.isBackupDeal && (
                      <Text fontSize="xs" color="purple.500">backup</Text>
                    )}
                  </Table.Cell>
                  <Table.Cell px={3} py={2}><Text fontSize="sm">{r.clientName}</Text></Table.Cell>
                  <Table.Cell px={3} py={2}><Text fontSize="sm">{r.fundName}</Text></Table.Cell>
                  <Table.Cell px={3} py={2}>
                    <Text fontSize="sm" color={r.isExited ? "green.600" : "text.muted"}>
                      {r.isExited ? fmtDate(r.exitDate) : "Live"}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="sm">{fmt$(r.totalInvestment)}</Text></Table.Cell>
                  <Table.Cell textAlign="right" px={3} py={2}>
                    <Text fontSize="sm" color={r.profit >= 0 ? undefined : "red.500"}>{fmt$(r.profit)}</Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="sm">{fmtPct(r.irr)}</Text></Table.Cell>
                  <Table.Cell textAlign="right" px={3} py={2}>
                    <Text fontSize="sm">{r.moic != null ? `${r.moic.toFixed(2)}x` : "—"}</Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="sm">{fmt$(r.calculatedCommission)}</Text></Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>
    </Stack>
  )
}
